import React, { memo, useEffect, useState } from 'react';
import { css, useTheme } from '@emotion/react';
import type { Entry } from 'contentful';
import type { ColourField, DynamicContentSlotContentType, RichTextContentType } from 'tsconfig/types';
import { globals } from '../../../utils/globals';
import { inspectorMode } from 'common-ui';
import { CmsRichText } from '../../contentful/CmsRichText/CmsRichText';
import {
  renderDynamicContentSlot,
  isRichTextType,
  isDynamicContentSlotType,
  hideStripBannerForPage,
} from '../stripBannerHelpers';

export type CmsStripBannerProps = {
  cmsStripBanner?: Entry<DynamicContentSlotContentType | RichTextContentType, 'WITHOUT_UNRESOLVABLE_LINKS', string>;
  cmsStripBannerBg?: ColourField;
  id?: string;
  pageSlug?: string;
  stripBannerDisabledPages?: string[];
};

export const StripBanner = memo((props: CmsStripBannerProps) => {
  const { cmsStripBannerBg, cmsStripBanner, pageSlug, stripBannerDisabledPages } = props || {};
  const contentTypeId = cmsStripBanner?.sys?.contentType?.sys?.id;
  const theme = useTheme();
  const widgetTheme = theme.widgets.GlobalHeader || {};

  const wrapper = css`
    ${widgetTheme.stripBanner}
    ${cmsStripBannerBg && `background-color: ${cmsStripBannerBg.value};`}
  `;
  // Default to false so SSR/SSG renders the banner
  const [isDWAPPMobile, setIsDWAPPMobile] = useState<boolean>(false);

  useEffect(() => {
    // DSP-1478 - hide stripe banner for userAgent=DWAPP and mobile only
    if (typeof window !== 'undefined') {
      const { userAgent } = navigator;

      // Check if the user agent contains "DWAPP"
      const isDWAPP = userAgent.includes('DWAPP');

      // Basic mobile check
      const isMobile = /Mobi|Android|iPhone|iPad|iPod|BlackBerry|Windows Phone|webOS|Opera Mini|IEMobile/.test(
        userAgent,
      );

      // Set state based on the checks
      setIsDWAPPMobile(isDWAPP && isMobile);
    }
  }, []);

  // Early return if banner should be hidden or no content
  if (
    globals.isEmbedded ||
    isDWAPPMobile ||
    !contentTypeId ||
    hideStripBannerForPage(pageSlug, stripBannerDisabledPages)
  ) {
    return null;
  }

  // Compute the content before rendering the wrapper
  const content = (() => {
    if (isRichTextType(cmsStripBanner)) {
      return <CmsRichText contentItem={cmsStripBanner} />;
    }
    if (isDynamicContentSlotType(cmsStripBanner)) {
      return renderDynamicContentSlot(cmsStripBanner);
    }
    return null;
  })();

  // Don't render empty wrapper for unsupported content types
  if (!content) {
    return null;
  }

  return (
    <div data-test="widget-StripBanner" css={wrapper} {...inspectorMode(props.id, 'stripBanner')}>
      {content}
    </div>
  );
});
